import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    CircularProgress,
    Alert,
    List,
    ListItemButton,
    Chip,
    IconButton,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    MenuItem,
    Tabs,
    Tab,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { API_URL } from '../../api';
import { useConfirm } from '../../components/ConfirmDialog';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const ROLE_LABEL = { parent: 'Parent', teacher: 'Tutor', student: 'Student' };
const ROLE_COLOR = { parent: 'primary', teacher: 'success', student: 'info' };

const fmtTime = (d) => {
    const date = new Date(d);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-GB');
};

const participantLabel = (p) => {
    if (!p) return '—';
    const name = `${p.firstName || ''} ${p.surname || ''}`.trim();
    if (p.role === 'parent' && p.childInfo?.studentName) {
        return `${name} (parent of ${p.childInfo.studentName})`;
    }
    return name;
};

export default function MessagingPage() {
    const confirm = useConfirm();
    const [conversations, setConversations] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [participant, setParticipant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chatLoading, setChatLoading] = useState(false);
    const [error, setError] = useState('');

    const [body, setBody] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [sending, setSending] = useState(false);
    const fileInput = useRef(null);
    const chatBody = useRef(null);

    // Role filter for the conversation list — all / parent / teacher / student.
    const [roleTab, setRoleTab] = useState('all');
    // Active vs Archived — mirrors the pattern used on academic years,
    // pending approvals, etc. Selecting a tab reloads the list.
    const [statusTab, setStatusTab] = useState('active');

    // "New message" dialog state
    const [newOpen, setNewOpen] = useState(false);
    const [newRole, setNewRole] = useState('parent');
    const [newAccounts, setNewAccounts] = useState([]);
    const [newAccount, setNewAccount] = useState(null);
    const [newBody, setNewBody] = useState('');
    const [newBusy, setNewBusy] = useState(false);
    const [newSearchBusy, setNewSearchBusy] = useState(false);

    const loadConversations = async () => {
        try {
            const qs = statusTab === 'archived' ? '?archived=true' : '';
            const { data } = await axios.get(
                `${API_URL}/api/admin/messaging/conversations${qs}`,
                authHeaders()
            );
            setConversations(data?.items || []);
        } catch {
            setError('Failed to load conversations.');
        } finally {
            setLoading(false);
        }
    };

    const archiveConversation = async (convId) => {
        try {
            await axios.post(
                `${API_URL}/api/admin/messaging/conversations/${convId}/archive`,
                {},
                authHeaders()
            );
            if (activeId === convId) {
                setActiveId(null);
                setMessages([]);
                setParticipant(null);
            }
            await loadConversations();
        } catch {
            setError('Failed to archive.');
        }
    };

    const unarchiveConversation = async (convId) => {
        try {
            await axios.post(
                `${API_URL}/api/admin/messaging/conversations/${convId}/unarchive`,
                {},
                authHeaders()
            );
            await loadConversations();
        } catch {
            setError('Failed to restore.');
        }
    };

    // Load accounts for the New-message picker, scoped by chosen role.
    const loadAccountsForRole = async (role) => {
        setNewSearchBusy(true);
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/accounts?role=${role}&limit=200&sort=surname`,
                authHeaders()
            );
            setNewAccounts(data?.items || []);
        } catch {
            setNewAccounts([]);
        } finally {
            setNewSearchBusy(false);
        }
    };

    const openNewDialog = () => {
        setNewOpen(true);
        setNewRole('parent');
        setNewAccount(null);
        setNewBody('');
        loadAccountsForRole('parent');
    };

    const submitNewMessage = async () => {
        if (!newAccount?._id) return;
        setNewBusy(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/messaging/start`,
                { accountId: newAccount._id, body: newBody.trim() },
                authHeaders()
            );
            setNewOpen(false);
            await loadConversations();
            // Open the just-created/reused conversation in the chat pane.
            if (data?.conversation?._id) openConversation(data.conversation);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to start conversation.');
        } finally {
            setNewBusy(false);
        }
    };

    useEffect(() => {
        loadConversations();
        const id = setInterval(loadConversations, 30000);
        return () => clearInterval(id);
    }, [statusTab]);

    const openConversation = async (conv) => {
        setActiveId(conv._id);
        setChatLoading(true);
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/messaging/conversations/${conv._id}/messages`,
                authHeaders()
            );
            setMessages(data.messages || []);
            setParticipant(data.conversation?.participantId || null);
            await loadConversations(); // refresh "unread" state
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load thread.');
        } finally {
            setChatLoading(false);
        }
    };

    // Scroll the chat container itself — never the whole page.
    useEffect(() => {
        if (chatBody.current) {
            chatBody.current.scrollTop = chatBody.current.scrollHeight;
        }
    }, [messages]);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 25 * 1024 * 1024) {
            setError('File is larger than 25 MB.');
            return;
        }
        try {
            const reader = new FileReader();
            reader.onload = async () => {
                try {
                    const { data } = await axios.post(
                        `${API_URL}/api/admin/uploads/file`,
                        { base64: reader.result, filename: file.name, folder: 'messaging' },
                        authHeaders()
                    );
                    setAttachments((a) => [
                        ...a,
                        { url: data.url, filename: data.filename || file.name, mime: file.type, size: data.size || file.size },
                    ]);
                } catch (err) {
                    setError(err?.response?.data?.message || 'Upload failed.');
                }
            };
            reader.readAsDataURL(file);
        } finally {
            if (fileInput.current) fileInput.current.value = '';
        }
    };

    const send = async () => {
        if (!activeId) return;
        if (!body.trim() && attachments.length === 0) return;
        setSending(true);
        try {
            await axios.post(
                `${API_URL}/api/admin/messaging/conversations/${activeId}/messages`,
                { body: body.trim(), attachments },
                authHeaders()
            );
            setBody('');
            setAttachments([]);
            await openConversation({ _id: activeId });
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to send.');
        } finally {
            setSending(false);
        }
    };

    return (
        <Container maxWidth="xl">
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Messages</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 640 }}>
                        Two-way messaging with parents, tutors and students. Click <strong>New message</strong>
                        {' '}to start a conversation with anyone in the system. Attachments up to 25 MB.
                    </Typography>
                    
                </Box>
                <Button
                    onClick={openNewDialog}
                    variant="contained"
                    startIcon={<EditNoteIcon />}
                    sx={{ flexShrink: 0 }}
                >
                    New message
                </Button>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> This is the two-way inbox with parents, students, and tutors — messages sent from their portals land here.<br />
                            <strong>2.</strong> Unread threads are highlighted and drive the sidebar badge count.<br />
                            <strong>3.</strong> Click a thread to read the conversation and reply. Replies push a notification to the recipient's portal.<br />
                            <strong>4.</strong> You can also start a new thread from a parent/student detail page — Messaging shortcut on the account.<br />
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 240px)' }}>
                <Card sx={{ width: 320, display: 'flex', flexDirection: 'column' }}>
                    {/* Role tabs — filter the list below */}
                    {(() => {
                        const counts = { all: conversations.length, parent: 0, teacher: 0, student: 0 };
                        const unread = { all: 0, parent: 0, teacher: 0, student: 0 };
                        for (const c of conversations) {
                            if (counts[c.participantRole] != null) counts[c.participantRole] += 1;
                            if (c._unread) {
                                unread.all += 1;
                                if (unread[c.participantRole] != null) unread[c.participantRole] += 1;
                            }
                        }
                        // Short labels with a subtle count + an unread red dot,
                        // tight enough to fit the 320px-wide column without overflow.
                        const labelFor = (v, label) => (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <span>{label}</span>
                                <Typography component="span" variant="caption" sx={{ opacity: 0.7 }}>
                                    {counts[v] || 0}
                                </Typography>
                                {unread[v] > 0 && (
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'error.main', ml: 0.25 }} />
                                )}
                            </Box>
                        );
                        return (
                            <>
                                <Tabs
                                    value={statusTab}
                                    onChange={(_, v) => setStatusTab(v)}
                                    variant="fullWidth"
                                    sx={{
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                        minHeight: 32,
                                        '& .MuiTab-root': {
                                            minHeight: 32,
                                            minWidth: 0,
                                            px: 0.5,
                                            fontSize: 11,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                        },
                                    }}
                                >
                                    <Tab value="active" label="Active" />
                                    <Tab value="archived" label="Archived" />
                                </Tabs>
                                <Tabs
                                    value={roleTab}
                                    onChange={(_, v) => setRoleTab(v)}
                                    variant="fullWidth"
                                    sx={{
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                        minHeight: 36,
                                        '& .MuiTab-root': {
                                            minHeight: 36,
                                            minWidth: 0,
                                            px: 0.5,
                                            fontSize: 11,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                        },
                                    }}
                                >
                                    <Tab value="all" label={labelFor('all', 'All')} />
                                    <Tab value="parent" label={labelFor('parent', 'Parents')} />
                                    <Tab value="teacher" label={labelFor('teacher', 'Tutors')} />
                                    <Tab value="student" label={labelFor('student', 'Students')} />
                                </Tabs>
                            </>
                        );
                    })()}

                    {loading ? (
                        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                    ) : (
                        <List dense sx={{ flex: 1, overflowY: 'auto' }}>
                            {conversations
                                .filter((c) => roleTab === 'all' || c.participantRole === roleTab)
                                .map((c) => (
                                <ListItemButton
                                    key={c._id}
                                    selected={c._id === activeId}
                                    onClick={() => openConversation(c)}
                                    sx={{ flexDirection: 'column', alignItems: 'stretch', py: 1.5 }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                            {c._unread && (
                                                <Chip
                                                    size="small"
                                                    label="New"
                                                    color="error"
                                                    sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                                                />
                                            )}
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: c._unread ? 700 : 400 }}
                                                noWrap
                                            >
                                                {participantLabel(c.participantId)}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            size="small"
                                            label={ROLE_LABEL[c.participantRole] || c.participantRole}
                                            color={ROLE_COLOR[c.participantRole] || 'default'}
                                            sx={{ ml: 1, flexShrink: 0 }}
                                        />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {c.lastMessageSnippet || '— no messages yet —'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {c.lastMessageAt ? fmtTime(c.lastMessageAt) : ''}
                                    </Typography>
                                </ListItemButton>
                            ))}
                            {conversations.length === 0 && (
                                <Box sx={{ p: 3 }}>
                                    <Typography color="text.secondary" align="center">
                                        No conversations yet.
                                    </Typography>
                                </Box>
                            )}
                            {conversations.length > 0 &&
                                conversations.filter((c) => roleTab === 'all' || c.participantRole === roleTab).length === 0 && (
                                <Box sx={{ p: 3 }}>
                                    <Typography color="text.secondary" align="center" variant="body2">
                                        No conversations with {roleTab === 'parent' ? 'parents' : roleTab === 'teacher' ? 'tutors' : 'students'} yet.
                                    </Typography>
                                </Box>
                            )}
                        </List>
                    )}
                </Card>

                <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {activeId ? (
                        <>
                            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h6">{participantLabel(participant)}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {participant?.email}
                                    </Typography>
                                </Box>
                                {(() => {
                                    const cur = conversations.find((c) => c._id === activeId);
                                    if (!cur) return null;
                                    return cur.isArchived ? (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => unarchiveConversation(activeId)}
                                        >
                                            Restore
                                        </Button>
                                    ) : (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="warning"
                                            onClick={async () => {
                                                if (await confirm({
                                                    title: 'Archive conversation',
                                                    message: 'It will move to the Archived tab and stop appearing in the default list. All messages are preserved.',
                                                    confirmLabel: 'Archive',
                                                })) {
                                                    archiveConversation(activeId);
                                                }
                                            }}
                                        >
                                            Archive
                                        </Button>
                                    );
                                })()}
                            </Box>
                            <Box ref={chatBody} sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: 'grey.50' }}>
                                {chatLoading ? (
                                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                                ) : (
                                    messages.map((m) => {
                                        const mine = m.senderRole === 'admin';
                                        return (
                                            <Box
                                                key={m._id}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: mine ? 'flex-end' : 'flex-start',
                                                    mb: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        maxWidth: '70%',
                                                        bgcolor: mine ? 'primary.main' : 'background.paper',
                                                        color: mine ? '#fff' : 'text.primary',
                                                        border: mine ? 0 : 1,
                                                        borderColor: 'divider',
                                                        borderRadius: 2,
                                                        p: 1.2,
                                                    }}
                                                >
                                                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{m.body}</Typography>
                                                    {(m.attachments || []).map((a) => (
                                                        <Box key={a.url} sx={{ mt: 0.5 }}>
                                                            <a href={a.url} target="_blank" rel="noreferrer" style={{ color: mine ? '#fff' : '#6D3FC5' }}>
                                                                📎 {a.filename}
                                                            </a>
                                                        </Box>
                                                    ))}
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                                                        {fmtTime(m.createdAt)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })
                                )}
                            </Box>
                            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                                {attachments.length > 0 && (
                                    <Box sx={{ mb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {attachments.map((a, i) => (
                                            <Chip
                                                key={i}
                                                size="small"
                                                label={`📎 ${a.filename}`}
                                                onDelete={() => setAttachments((arr) => arr.filter((_, j) => j !== i))}
                                            />
                                        ))}
                                    </Box>
                                )}
                                <Stack direction="row" spacing={1}>
                                    <IconButton onClick={() => fileInput.current?.click()}>
                                        <AttachFileIcon />
                                    </IconButton>
                                    <input type="file" hidden ref={fileInput} onChange={handleFile} />
                                    <TextField
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        placeholder="Type a message…"
                                        size="small"
                                        fullWidth
                                        multiline
                                        maxRows={4}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                send();
                                            }
                                        }}
                                    />
                                    <Button onClick={send} variant="contained" disabled={sending} endIcon={<SendIcon />}>
                                        Send
                                    </Button>
                                </Stack>
                            </Box>
                        </>
                    ) : (
                        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
                            <Typography color="text.secondary">Pick a conversation on the left.</Typography>
                        </Stack>
                    )}
                </Card>
            </Box>

            {/* New message dialog */}
            <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New message</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Pick who you want to message. If a conversation already exists with that
                            person you'll be taken to it; otherwise a new thread opens.
                        </Typography>
                        <TextField
                            select size="small" label="Audience"
                            value={newRole}
                            onChange={(e) => {
                                setNewRole(e.target.value);
                                setNewAccount(null);
                                loadAccountsForRole(e.target.value);
                            }}
                        >
                            <MenuItem value="parent">Parents</MenuItem>
                            <MenuItem value="teacher">Tutors</MenuItem>
                            <MenuItem value="student">Students</MenuItem>
                        </TextField>
                        <Autocomplete
                            options={newAccounts}
                            loading={newSearchBusy}
                            value={newAccount}
                            onChange={(_, v) => setNewAccount(v)}
                            getOptionLabel={(o) => {
                                const name = `${o.firstName || ''} ${o.surname || ''}`.trim();
                                const child = o.role === 'parent' && o.childInfo?.studentName
                                    ? ` (parent of ${o.childInfo.studentName})`
                                    : '';
                                return `${name}${child} — ${o.email}`;
                            }}
                            isOptionEqualToValue={(o, v) => o._id === v._id}
                            renderInput={(params) => (
                                <TextField {...params} label="Recipient" size="small" placeholder="Search by name or email" />
                            )}
                        />
                        <TextField
                            label="Message (optional)"
                            multiline minRows={3}
                            value={newBody}
                            onChange={(e) => setNewBody(e.target.value)}
                            size="small"
                            placeholder="You can leave this empty to just open a blank thread."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewOpen(false)} disabled={newBusy}>Cancel</Button>
                    <Button
                        onClick={submitNewMessage}
                        variant="contained"
                        disabled={!newAccount || newBusy}
                    >
                        {newBusy ? 'Opening…' : (newBody.trim() ? 'Send message' : 'Open thread')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}